// Lib
import { DependencyList, useCallback, SyntheticEvent } from 'react';
import { createDispatchHook, createSelectorHook, Selector, useSelector } from 'react-redux';
import {
    EditorInstanceObject,
    TiptapEditorRegistryCallback,
    TiptapEditorStoreActions,
    TiptapEditorStoreState,
} from './tiptapEditorStoreTypes';

// Context
import { TiptapEditorStoreContext } from './TiptapEditorStoreProvider';

// Actions
import { getActiveTiptapEditorThunk, getTiptapEditorByIdThunk } from './tiptapEditorStoreActions';
import { Editor } from '@tiptap/react';
import { getIsTouchSelector } from '../../../platform/platformSelector';

// FIXME-Tiptap thunk typings
export const useTiptapEditorDispatch = createDispatchHook<TiptapEditorStoreState, TiptapEditorStoreActions>(
    TiptapEditorStoreContext,
);

const tiptapSelectorHook = createSelectorHook<TiptapEditorStoreState, TiptapEditorStoreActions>(
    TiptapEditorStoreContext,
);

export type ActiveTiptapEditorSelector = Selector<Editor | undefined, any>;

/**
 * This hook allows consumers to select values from the active tiptap editor, without needing
 * to worry about finding the instance within the store's structure.
 *
 * The use of "activeEditor.instance" is so that we can ensure a re-render on active editor changes.
 * The activeEditor is a stable reference, so we need to wrap it in an object to ensure the reference changes.
 *
 * This hook allows selectors to ignore this detail.
 */
export const useTiptapActiveEditorSelector = (selector: ActiveTiptapEditorSelector) =>
    // @ts-ignore We're passing activeEditor as the third argument here, in case a createSelector is being used.
    //    This will force the selector to re-run even though the instance reference is stable.
    tiptapSelectorHook((state) => selector(state.activeEditor?.instance, undefined, state.activeEditor));

/**
 * This hook allows the consumer to get the active Tiptap editor instance directly,
 * however it is marked as "dangerous" because it could cause performance issues if it's used
 * in components that are regularly rendered.
 *
 * This is because the active editor instance can change frequently, so if many components use
 * this, it could cause many unnecessary re-renders.
 */
export const dangerouslyUseTiptapActiveEditor = () => tiptapSelectorHook((state) => state.activeEditor)?.instance;

type ActiveTiptapEditorCallback = (editor?: Editor, ...args: any[]) => unknown;
type UseActiveTiptapEditorCallback = (
    func: ActiveTiptapEditorCallback,
    dependencies?: DependencyList,
) => (...args: any[]) => unknown;

/**
 * Fires a thunk to get the active tiptap editor from the tiptap editor store,
 * then provides the editor to the callback function.
 */
export const useActiveTiptapEditorCallback: UseActiveTiptapEditorCallback = (func, dependencies) => {
    const dispatch = useTiptapEditorDispatch();

    const returnFunc = (...args: unknown[]) => {
        // @ts-ignore Tiptap thunk typings - FIXME
        const activeEditor = dispatch(getActiveTiptapEditorThunk()) as EditorInstanceObject | null;
        return func(activeEditor?.instance, ...args);
    };

    return dependencies ? useCallback(returnFunc, dependencies) : returnFunc;
};

/**
 * Returns a callback that resolves a Tiptap editor instance by its editor ID.
 *
 * This is useful if the editor isn't the active editor, so the activeEditor state isn't set,
 * but we still need a reference to the Tiptap editor, for some reason.
 * Such as when removing labels from task items - selecting the label chip will mean the
 * editor is no longer active.
 */
export const useTiptapEditorByIdCallback = (): TiptapEditorRegistryCallback => {
    const dispatch = useTiptapEditorDispatch();

    return useCallback(
        (editorId: string) => {
            // @ts-ignore Tiptap thunk typings
            const editor = dispatch(
                // @ts-ignore Tiptap thunk typings
                getTiptapEditorByIdThunk(editorId),
            ) as EditorInstanceObject | null;
            return editor?.instance;
        },
        [dispatch],
    );
};

type ActiveTiptapEditorResolvedCallback = (editor: Editor, ...args: any[]) => unknown;
export const useActiveTiptapEditorTouchCallback = (
    callback: ActiveTiptapEditorResolvedCallback,
    dependencies: DependencyList,
) => {
    const isTouchDevice = useSelector(getIsTouchSelector);

    return useActiveTiptapEditorCallback(
        (editor, ...args) => {
            if (!editor) return;

            const result = callback(editor, ...args);

            if (isTouchDevice) editor.commands.focus(undefined, { scrollIntoView: false });

            return result;
        },
        [isTouchDevice, ...(dependencies || [])],
    );
};

export const useFocusActiveTiptapEditorOnTouch = (dependencies?: DependencyList) => {
    const isTouchDevice = useSelector(getIsTouchSelector);

    return useActiveTiptapEditorCallback(
        (editor, _isOpen: boolean, event?: SyntheticEvent) => {
            if (!editor || !event || !isTouchDevice) return;

            editor.commands.focus(undefined, { scrollIntoView: false });
        },
        [isTouchDevice, ...(dependencies || [])],
    );
};
